import { useState, useRef } from 'react';
import { Upload, Loader2, Eye, X } from 'lucide-react';
import { uploadAndProcess, getUploadTextPreview } from '../services/api';
import { useExpenses } from '../contexts/ExpenseContext';
import type { ExpenseCategory } from '../types';

type ProcessItem = { id: string; status: string; errors?: string; hint?: string };

interface BulkUploadSectionProps {
  category: ExpenseCategory;
  targetYear?: number | 'auto';
  onError?: (message: string) => void;
}

export default function BulkUploadSection({ category, targetYear = 'auto', onError }: BulkUploadSectionProps) {
  const { refreshExpenses } = useExpenses();
  const [bulkFilesCount, setBulkFilesCount] = useState(0);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState<{
    imported: number;
    failed: number;
    skipped: number;
    errors: string[];
    parseErrorItems: ProcessItem[];
    nameById: Map<string, string>;
  } | null>(null);
  const [previewModal, setPreviewModal] = useState<{ uploadId: string; name: string } | null>(null);
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const bulkFileInputRef = useRef<HTMLInputElement>(null);

  const formatParseError = (errors?: string, hint?: string): string => {
    const code = (errors || '').trim();
    let label = '';
    switch (code) {
      case 'pdf_has_no_extractable_text':
        label = 'No extractable text (needs OCR)';
        break;
      case 'unrecognized_settlement_format':
        label = 'Unknown settlement format / carrier';
        break;
      case 'unknown_document':
        label = 'Unknown document / carrier';
        break;
      case 'batch_failed':
        label = 'Database write failed';
        break;
      case 'duplicate_settlement':
        label = 'Duplicate settlement (already imported)';
        break;
      default:
        if (code) label = code.replace(/[_\-]+/g, ' ');
    }
    const hintText = (hint || '').trim();
    if (hintText && !/needs_ocr/i.test(hintText)) {
      return label ? `${label} — ${hintText}` : hintText;
    }
    return label || 'Parse failed';
  };

  const handleBulkUpload = async () => {
    const input = bulkFileInputRef.current;
    if (!input?.files || input.files.length === 0) return;
    const files = Array.from(input.files);
    setIsBulkUploading(true);
    setBulkResult(null);
    onError?.('');
    try {
      const { uploadResult, processResult } = await uploadAndProcess(category, files, targetYear);
      const okCount = processResult.items.filter((i) => i.status === 'ok').length;
      const failCount = processResult.items.filter((i) => i.status === 'error').length;
      const skipCount = processResult.items.filter((i) =>
        ['skipped', 'no_match', 'parse_error'].includes(i.status)
      ).length;
      const errors = processResult.items.filter((i) => i.errors).map((i) => i.errors!);
      const parseErrorItems = processResult.items.filter((i) =>
        ['no_match', 'parse_error'].includes(i.status)
      );
      const nameById = new Map(uploadResult.items.map((x) => [x.id, x.original_name]));
      setBulkResult({
        imported: okCount,
        failed: failCount,
        skipped: skipCount,
        errors,
        parseErrorItems,
        nameById,
      });
      await refreshExpenses();
      input.value = '';
      setBulkFilesCount(0);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Bulk upload failed');
    } finally {
      setIsBulkUploading(false);
    }
  };

  const handleViewPreview = async (uploadId: string, name: string) => {
    setPreviewModal({ uploadId, name });
    setPreviewContent(null);
    setPreviewLoading(true);
    try {
      const res = await getUploadTextPreview(uploadId);
      setPreviewContent(res.textPreview || '(empty)');
    } catch {
      setPreviewContent('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20">
      <div className="flex items-center gap-3 mb-3">
        <Upload className="w-5 h-5 text-shuttle-sand" />
        <h3 className="text-base font-semibold text-white">Bulk Upload</h3>
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-sm text-white/60 mb-1">Files</label>
          <input
            ref={bulkFileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf"
            onChange={(e) => setBulkFilesCount(e.target.files?.length ?? 0)}
            className="block w-full text-sm text-white/70 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-shuttle-sand file:text-shuttle-dark file:font-medium"
          />
        </div>
        <button
          onClick={handleBulkUpload}
          disabled={isBulkUploading || bulkFilesCount === 0}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isBulkUploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              Bulk Upload
            </>
          )}
        </button>
      </div>
      {bulkResult && (
        <div className="mt-3 text-sm text-white/80 space-y-2">
          <p>Success: {bulkResult.imported} · Skipped: {bulkResult.skipped} · Failed: {bulkResult.failed}</p>
          {bulkResult.parseErrorItems.length > 0 && (
            <div className="space-y-1">
              {bulkResult.parseErrorItems.map((item) => (
                <div key={item.id} className="flex items-center gap-2 flex-wrap text-xs">
                  <span className="text-red-300">
                    {bulkResult!.nameById.get(item.id) ?? item.id}: {formatParseError(item.errors, item.hint)}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleViewPreview(item.id, bulkResult!.nameById.get(item.id) ?? item.id)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/10 hover:bg-white/20 text-white/90"
                  >
                    <Eye className="w-3 h-3" />
                    View text preview
                  </button>
                </div>
              ))}
            </div>
          )}
          {bulkResult.errors.length > 0 && bulkResult.parseErrorItems.length === 0 && (
            <span className="block text-red-300 text-xs">{bulkResult.errors.slice(0, 3).join('; ')}</span>
          )}
        </div>
      )}
      {previewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setPreviewModal(null)}>
          <div className="bg-shuttle-dark rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b border-white/20">
              <h3 className="font-semibold text-white truncate">{previewModal.name} — Text preview</h3>
              <button type="button" onClick={() => setPreviewModal(null)} className="p-1 rounded hover:bg-white/10">
                <X className="w-5 h-5" />
              </button>
            </div>
            <pre className="flex-1 overflow-auto p-4 text-xs text-white/80 whitespace-pre-wrap font-mono">
              {previewLoading ? 'Loading...' : previewContent ?? ''}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
